import { ComponentProps, ReactNode, forwardRef } from 'react';

import classNames from 'classnames';

import { Button, DialogFooter, Tooltip, TooltipTrigger } from '@backstage/ui';

import { Spinner } from '../Spinner';
import {
  SplitButton,
  SplitButtonOptionProps,
  SplitButtonProps,
} from '../SplitButton';
import styles from './ActionsBui.module.css';
import { useDialogContext } from './context';

type ButtonBuiProps = ComponentProps<typeof Button> & {
  onPress?: () => void;
};

const Cancel = (props: ButtonBuiProps) => {
  const { onClose, isLoading } = useDialogContext();
  const { children = 'Cancel', onPress, isDisabled, ...rest } = props;

  const handlePress = () => {
    onPress?.();
    onClose();
  };

  return (
    <Button
      variant="secondary"
      onPress={handlePress}
      isDisabled={isLoading || isDisabled}
      data-track-id="button.cancel"
      data-track-action="dismiss"
      {...rest}
    >
      {children}
    </Button>
  );
};

type PrimaryBuiProps = ButtonBuiProps & { tooltip?: string };

const Primary = ({ tooltip, ...props }: PrimaryBuiProps) => {
  const { isLoading } = useDialogContext();
  const { children = 'Save', isDisabled, ...rest } = props;

  const button = (
    <Button
      variant="primary"
      data-testid="primary-button"
      isDisabled={isLoading || isDisabled}
      isPending={isLoading}
      data-track-id="button.submit"
      data-track-action="update"
      {...rest}
    >
      {children}
    </Button>
  );

  if (tooltip) {
    return (
      <TooltipTrigger>
        {button}
        <Tooltip>{tooltip}</Tooltip>
      </TooltipTrigger>
    );
  }

  return button;
};

const Secondary = (props: ButtonBuiProps) => {
  const { isLoading } = useDialogContext();
  const { children = 'Create', isDisabled, ...rest } = props;

  return (
    <Button
      variant="secondary"
      data-testid="secondary-button"
      isDisabled={isLoading || isDisabled}
      isPending={isLoading}
      data-track-id="button.secondary"
      data-track-action="update"
      {...rest}
    >
      {children}
    </Button>
  );
};

const MultiActionOption = (props: SplitButtonOptionProps) => {
  const { isLoading } = useDialogContext();
  const { disabled, ...rest } = props;

  return <SplitButton.Option {...rest} disabled={isLoading || disabled} />;
};

// SplitButton itself has no BUI equivalent yet, so MultiAction wraps it until
// the SplitButton migration lands. The loading indicator is the shared BUI
// Spinner (BUI has no spinner/progress primitive) rather than MUI CircularProgress.
const MultiAction = (props: SplitButtonProps) => {
  const { isLoading } = useDialogContext();
  const { disabled, ...rest } = props;

  return (
    <SplitButton
      color="primary"
      variant="contained"
      data-testid="multi-action-button"
      {...rest}
      {...(isLoading && {
        endIcon: <Spinner data-testid="progress" />,
      })}
      disabled={isLoading || disabled}
    />
  );
};

MultiAction.Option = MultiActionOption;

const Submit = (props: PrimaryBuiProps) => {
  return <Primary type="submit" {...props} />;
};

export type ActionsBuiProps = {
  className?: string;
  children?: ReactNode;
};

const Actions = forwardRef<HTMLDivElement, ActionsBuiProps>((props, ref) => {
  const { className, ...rest } = props;

  return (
    <DialogFooter
      ref={ref}
      className={classNames(styles.root, className)}
      {...rest}
    />
  );
});

Actions.displayName = 'DialogCompositeBui.Actions';

export const ActionsBui = Object.assign(Actions, {
  Cancel,
  Submit,
  Secondary,
  Primary,
  MultiAction,
});

export default ActionsBui;
